import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  ArrowLeft, 
  Edit2, 
  Ticket, 
  DollarSign, 
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Play,
  Pause,
  Send,
  BarChart3,
  FileCheck,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const ManageEvent = ({ user }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showChecklist, setShowChecklist] = useState(false);

  // Get event details
  const { data: event, isLoading, refetch } = useQuery({
    queryKey: ["promoterEvent", eventId],
    queryFn: async () => {
      const res = await axios.get(`${API}/events/${eventId}/sales`);
      const eventRes = await axios.get(`${API}/events/${eventId}`);
      return { ...eventRes.data, sales: res.data };
    }
  });

  // Get verification checklist
  const { data: checklist } = useQuery({
    queryKey: ["eventChecklist", eventId],
    queryFn: async () => {
      const res = await axios.get(`${API}/promoter/events/${eventId}/checklist`);
      return res.data;
    }
  });

  // Submit for review mutation
  const submitForReviewMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post(`${API}/promoter/events/${eventId}/submit-for-review`);
      return res.data;
    },
    onSuccess: (data) => {
      if (data.verification?.approved) {
        toast.success("Event approved! You can now go live.");
      } else {
        toast.error("Event needs attention. Check the verification feedback.");
      }
      refetch();
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to submit for review");
    }
  });

  // Go live mutation
  const goLiveMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post(`${API}/promoter/events/${eventId}/go-live`);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Event is now live!");
      refetch();
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to go live");
    }
  });

  // Pause/Resume sales
  const toggleSalesMutation = useMutation({
    mutationFn: async (pause) => {
      const endpoint = pause ? "pause-sales" : "resume-sales";
      const res = await axios.post(`${API}/events/${eventId}/${endpoint}`);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      refetch();
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white p-4">
        <p>Event not found</p>
      </div>
    );
  }

  const status = event.status || (event.is_active ? "live" : "draft");
  const canSubmitForReview = status === "draft" || status === "rejected";
  const canGoLive = status === "approved";
  const isLive = status === "live" || event.is_active;

  const statusConfig = {
    draft: { color: "bg-zinc-500/20 text-zinc-400", icon: Edit2, text: "Draft" },
    pending_review: { color: "bg-amber-500/20 text-amber-400", icon: Clock, text: "Pending Review" },
    approved: { color: "bg-blue-500/20 text-blue-400", icon: CheckCircle2, text: "Approved" },
    live: { color: "bg-emerald-500/20 text-emerald-400", icon: Play, text: "Live" },
    rejected: { color: "bg-red-500/20 text-red-400", icon: XCircle, text: "Needs Attention" }
  };

  const StatusIcon = statusConfig[status]?.icon || Clock;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-24">
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate("/promoter")}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold truncate">{event.name}</h1>
            <p className="text-xs text-muted-foreground">
              {new Date(event.event_date).toLocaleDateString()} • {event.venue}
            </p>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs ${statusConfig[status]?.color}`}>
            <StatusIcon className="w-3 h-3" />
            {statusConfig[status]?.text}
          </div>
        </div>

        {/* Event Image */}
        {event.flyer_url && (
          <div className="mb-6 rounded-xl overflow-hidden">
            <img src={event.flyer_url} alt={event.name} className="w-full h-48 object-cover" />
          </div>
        )}

        {/* Sales Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="glass-light rounded-xl p-3 text-center">
            <Ticket className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
            <p className="text-lg font-bold">{event.sales?.summary?.total_tickets_sold || 0}</p>
            <p className="text-[10px] text-muted-foreground">Tickets Sold</p>
          </div>
          <div className="glass-light rounded-xl p-3 text-center">
            <DollarSign className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <p className="text-lg font-bold">J${(event.sales?.summary?.total_revenue || 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">Revenue</p>
          </div>
          <div className="glass-light rounded-xl p-3 text-center">
            <Users className="w-5 h-5 text-purple-400 mx-auto mb-1" />
            <p className="text-lg font-bold">{Object.keys(event.sales?.tier_breakdown || {}).length}</p>
            <p className="text-[10px] text-muted-foreground">Ticket Tiers</p>
          </div>
        </div>

        {/* Verification Feedback */}
        {event.verification_result && (
          <div className={`glass-light rounded-xl p-4 mb-6 border-l-4 ${
            event.verification_result.approved ? "border-emerald-500" : "border-red-500"
          }`}>
            <div className="flex items-start gap-3">
              {event.verification_result.approved ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
              )}
              <div>
                <p className="font-semibold text-sm">
                  {event.verification_result.approved ? "Verification Passed" : "Verification Issues"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{event.verification_result.summary}</p>
                
                {event.verification_result.missing_required?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-red-400 font-medium">Missing Required:</p>
                    <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                      {event.verification_result.missing_required.map((item, i) => (
                        <li key={i}>• {item.replace(/_/g, " ")}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {event.verification_result.suggestions?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-cyan-400 font-medium">Suggestions:</p>
                    <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                      {event.verification_result.suggestions.filter(Boolean).map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Verification Checklist */}
        {checklist && (
          <div className="glass-light rounded-xl mb-6 overflow-hidden">
            <button 
              onClick={() => setShowChecklist(!showChecklist)}
              className="w-full p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-pink-400" />
                <div className="text-left">
                  <p className="font-semibold text-sm">Verification Checklist</p>
                  <p className="text-xs text-muted-foreground">{checklist.completion_percentage}% complete</p>
                </div>
              </div>
              {showChecklist ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showChecklist && (
              <div className="px-4 pb-4 space-y-3">
                {Object.entries(checklist.checklist).map(([category, items]) => (
                  <div key={category}>
                    <p className="text-xs text-muted-foreground capitalize mb-2">{category.replace(/_/g, " ")}</p>
                    {Object.entries(items).map(([key, item]) => (
                      typeof item === 'object' && item.label && (
                        <div key={key} className="flex items-center gap-2 py-1">
                          {item.provided ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          ) : item.required ? (
                            <XCircle className="w-4 h-4 text-red-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-zinc-500" />
                          )}
                          <span className={`text-xs ${item.provided ? "text-white" : "text-muted-foreground"}`}>
                            {item.label} {item.required && "*"}
                          </span>
                        </div>
                      )
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tier Breakdown */}
        {event.sales?.tier_breakdown && Object.keys(event.sales.tier_breakdown).length > 0 && (
          <div className="glass-light rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-400" />
              Sales by Tier
            </h3>
            <div className="space-y-3">
              {Object.entries(event.sales.tier_breakdown).map(([tierName, data]) => (
                <div key={tierName}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span>{tierName}</span>
                    <span className="text-muted-foreground">{data.sold} / {data.total}</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"
                      style={{ width: `${(data.sold / data.total) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {canSubmitForReview && (
            <Button 
              onClick={() => submitForReviewMutation.mutate()}
              className="w-full btn-primary flex items-center justify-center gap-2"
              disabled={submitForReviewMutation.isPending || !checklist?.ready_for_review}
            >
              <Send className="w-4 h-4" />
              {submitForReviewMutation.isPending ? "Submitting..." : "Submit for AI Review"}
            </Button>
          )}

          {canGoLive && (
            <Button 
              onClick={() => goLiveMutation.mutate()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center gap-2"
              disabled={goLiveMutation.isPending}
            >
              <Play className="w-4 h-4" />
              {goLiveMutation.isPending ? "Going Live..." : "Go Live"}
            </Button>
          )}

          {isLive && (
            <Button 
              onClick={() => toggleSalesMutation.mutate(!event.is_active)}
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
              disabled={toggleSalesMutation.isPending}
            >
              {event.is_active ? (
                <>
                  <Pause className="w-4 h-4" />
                  Pause Sales
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Resume Sales
                </>
              )}
            </Button>
          )}

          <Button 
            onClick={() => navigate(`/promoter/events/${eventId}/edit`)}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            disabled={isLive && event.is_active}
          >
            <Edit2 className="w-4 h-4" />
            Edit Event
          </Button>
        </div>

        {!checklist?.ready_for_review && canSubmitForReview && (
          <p className="text-xs text-center text-amber-400 mt-3">
            Complete all required fields before submitting for review
          </p>
        )}
      </div>
      <NavBar active="lifestyle" />
    </div>
  );
};

export default ManageEvent;
